import { useState, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { applySeo } from "@/lib/seo";
import { trpc } from "@/lib/trpc";
import { BarometerNav } from "@/components/BarometerNav";
import { CrossSelling } from "@/components/CrossSelling";
import { generateECommercePdf } from "@/lib/generatePdf";
import { BAROMETER_PRICES, BAROMETER_MIT_VIDEOCALL, formatPrice } from "@shared/prices";
import { toast } from "sonner";

const C = {
  accent: "#4f46e5",
  accentDark: "#3730b3",
  accentLight: "#4f46e512",
  teal: "#115751",
  bg: "#ffffff",
  bgGray: "#f2f2f2",
  text: "#000000",
  muted: "#6b7280",
  border: "#e5e7eb",
};

const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";
const MEETERGO_URL = "https://cal.meetergo.com/larissa-girenko/30-min-meeting-or-larissa-girenko?utm_source=ig&utm_medium=social&utm_content=link_in_bio";

function LoadingScreen({ url }: { url: string }) {
  const steps = [
    { label: "Website laden & Screenshot erstellen", icon: "/manus-storage/11_094992a3.svg" },
    { label: "Seitentyp erkennen (Shop/Dienstleister)", icon: "/manus-storage/23_1ab7147e.svg" },
    { label: "Vertrauen & Trust-Signale prüfen", icon: "/manus-storage/29_7f3bb394.svg" },
    { label: "Produkt-/Leistungsdarstellung analysieren", icon: "/manus-storage/21_453003f0.svg" },
    { label: "Checkout & Conversion-Prozess bewerten", icon: "/manus-storage/21_453003f0.svg" },
    { label: "Mobile & Performance messen", icon: "/manus-storage/33_d51b7819.svg" },
    { label: "SEO & Auffindbarkeit prüfen", icon: "/manus-storage/33_d51b7819.svg" },
    { label: "Preispsychologie & Positionierung analysieren", icon: "/manus-storage/11_094992a3.svg" },
    { label: "E-Commerce-Score berechnen", icon: "/manus-storage/32_04885248.svg" },
  ];
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((s) => (s + 1) % steps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <div className="w-full max-w-md text-center">
        <div className="text-5xl mb-6">🛒</div>
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent, letterSpacing: "0.05em" }}>
          E-COMMERCE-ANALYSE LÄUFT
        </h2>
        <p className="text-sm mb-8" style={{ color: C.muted }}>
          Analysiere <strong>{url}</strong> auf 120+ E-Commerce-Kriterien
        </p>
        <div className="space-y-3 text-left">
          {steps.map((step, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg transition-all duration-500"
              style={{ background: i === activeStep ? C.accentLight : "transparent", border: `1px solid ${i === activeStep ? C.accent : "transparent"}` }}>
              <img src={step.icon} alt="" className="w-6 h-6 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
              <span className="text-sm font-medium" style={{ color: i === activeStep ? C.accent : C.muted }}>
                {step.label}
              </span>
              {i < activeStep && <span className="ml-auto text-green-500 text-sm">✓</span>}
              {i === activeStep && <span className="ml-auto text-xs animate-pulse" style={{ color: C.accent }}>läuft…</span>}
            </div>
          ))}
        </div>
        <p className="text-xs mt-6" style={{ color: C.muted }}>
          Bitte warte – die Analyse dauert ca. 30–60 Sekunden
        </p>
      </div>
    </div>
  );
}

/// ── Vollversion-Ergebnisansichtt ───────────────────────────────────────────────
interface FullECommerceData {
  url: string;
  domain?: string;
  page_title?: string;
  site_type?: string;
  total_score: number;
  max_score: number;
  reifegrad: { note: string; label: string; prozent: number; site_type_label: string };
  scores: Record<string, { label: string; score: number; max: number; details?: Record<string, unknown> }>;
  optimierungen: Array<{ dimension: string; kriterium: string; empfehlung: string; prioritaet: string }>;
  staerken: Array<{ dimension: string; text: string }>;
}

function FullECommerceResults({ data, onReset }: { data: FullECommerceData; onReset: () => void }) {
  const [activeTab, setActiveTab] = useState<"overview" | "dimensions" | "recommendations">("overview");
  const pct = data.reifegrad?.prozent ?? Math.round((data.total_score / (data.max_score || 120)) * 100);
  const grade = data.reifegrad?.note ?? "C";
  const gradeLabel = data.reifegrad?.label ?? "Ausbaufähig";
  const siteTypeLabel = data.reifegrad?.site_type_label ?? "Website";
  const gradeColor = grade === "A" ? "#16a34a" : grade === "B" ? "#2563eb" : grade === "C" ? "#d97706" : grade === "D" ? "#dc2626" : "#7c3aed";
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (pct / 100) * circumference;

  const priorityLabel = (p: string) => {
    if (p === "kritisch") return { label: "Kritisch", color: "#dc2626" };
    if (p === "hoch") return { label: "Hoch", color: "#ea580c" };
    return { label: "Mittel", color: "#ca8a04" };
  };

  const dimEntries = Object.entries(data.scores ?? {});

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/ecommerce-barometer" accentColor={C.accent} />
      <div style={{ height: "4px", backgroundColor: C.accent }} />

      {/* Ergebnis-Hero */}
      <section className="bg-black px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="54" fill="none" stroke="#1f2937" strokeWidth="12" />
                <circle cx="70" cy="70" r="54" fill="none" stroke={gradeColor} strokeWidth="12"
                  strokeDasharray={circumference} strokeDashoffset={offset}
                  strokeLinecap="round" transform="rotate(-90 70 70)" />
                <text x="70" y="62" textAnchor="middle" fill="white" fontSize="28" fontWeight="900" fontFamily="'Bebas Neue', sans-serif">{pct}</text>
                <text x="70" y="82" textAnchor="middle" fill="#9ca3af" fontSize="13" fontFamily="'DM Sans', sans-serif">von 100</text>
              </svg>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.accent }}>E-Commerce-Barometer™ Vollversion</p>
              <h1 className="font-black uppercase text-white mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 5vw, 3rem)" }}>{data.domain ?? data.url}</h1>
              <p className="text-gray-400 text-sm mb-4">{data.total_score} von {data.max_score} Punkten · {gradeLabel} · {siteTypeLabel}</p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: gradeColor, borderColor: gradeColor }}>{pct} Punkte – {gradeLabel}</span>
                <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: C.accent, borderColor: C.accent }}>{siteTypeLabel}</span>
              </div>
            </div>
            <button onClick={onReset} className="text-xs font-bold uppercase tracking-widest px-6 py-3 border border-gray-600 text-gray-300 hover:border-white hover:text-white transition-colors">
              Neue Analyse
            </button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex">
          {(["overview", "dimensions", "recommendations"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="px-6 py-4 text-xs font-bold uppercase tracking-widest transition-colors border-b-2"
              style={{
                borderColor: activeTab === tab ? C.accent : "transparent",
                color: activeTab === tab ? C.accent : "#6b7280",
              }}>
              {tab === "overview" ? "Übersicht" : tab === "dimensions" ? "Dimensionen" : "Empfehlungen"}
            </button>
          ))}
        </div>
      </div>

      {/* Tab-Inhalt */}
      <div className="max-w-4xl mx-auto w-full px-6 py-10">
        {activeTab === "overview" && (
          <div>
            <h2 className="font-black uppercase mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.8rem" }}>8 DIMENSIONEN IM ÜBERBLICK</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {dimEntries.map(([key, dim]) => {
                const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
                const col = dp >= 70 ? "#16a34a" : dp >= 40 ? "#d97706" : "#dc2626";
                return (
                  <div key={key} className="border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-widest" style={{ color: C.text }}>{dim.label}</span>
                      <span className="font-black text-lg" style={{ color: col, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.score}/{dim.max}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100">
                      <div className="h-1.5 transition-all" style={{ width: `${dp}%`, backgroundColor: col }} />
                    </div>
                    <p className="text-xs mt-1" style={{ color: "#6b7280" }}>{dp}% – {dp >= 70 ? "Gut" : dp >= 40 ? "Ausbaufähig" : "Kritisch"}</p>
                  </div>
                );
              })}
            </div>
            {/* Stärken */}
            {data.staerken?.length > 0 && (
              <div className="border border-green-200 p-6 mb-6" style={{ background: "#f0fdf4" }}>
                <h3 className="font-black uppercase mb-4 text-green-800" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>✅ Deine Stärken</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.staerken.map((s, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">✓</span>
                      <div>
                        <span className="text-xs font-bold text-green-700">{s.dimension}: </span>
                        <span className="text-xs text-green-700">{s.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "dimensions" && (
          <div className="space-y-6">
            {dimEntries.map(([key, dim]) => {
              const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
              const col = dp >= 70 ? "#16a34a" : dp >= 40 ? "#d97706" : "#dc2626";
              return (
                <div key={key} className="border border-gray-200">
                  <div className="px-6 py-4 flex items-center justify-between" style={{ background: "#111" }}>
                    <span className="font-black uppercase text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{dim.label}</span>
                    <span className="font-black text-xl" style={{ color: col, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.score}/{dim.max} ({dp}%)</span>
                  </div>
                  <div className="h-1" style={{ background: col, width: `${dp}%` }} />
                  <div className="px-6 py-4">
                    <p className="text-xs text-gray-500">{dp >= 70 ? "Gut aufgestellt in dieser Dimension." : dp >= 40 ? "Ausbaufähig – konkrete Maßnahmen in den Empfehlungen." : "Kritischer Handlungsbedarf – priorisiert angehen."}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === "recommendations" && (
          <div>
            <h2 className="font-black uppercase mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.8rem" }}>ALLE OPTIMIERUNGSEMPFEHLUNGEN</h2>
            <div className="space-y-4">
              {(data.optimierungen ?? []).map((opt, i) => {
                const p = priorityLabel(opt.prioritaet);
                return (
                  <div key={i} className="border border-gray-200 p-5">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: p.color }}>{p.label}</span>
                          <span className="text-xs text-gray-400">{opt.dimension}</span>
                        </div>
                        <p className="text-sm font-bold" style={{ color: C.text }}>{opt.kriterium}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 border-l-2 pl-3" style={{ borderColor: C.accent }}>{opt.empfehlung}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <section className="border-t border-gray-200 py-12 px-6 text-center">
        <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent }}>E-Commerce optimieren</p>
        <h2 className="font-black uppercase mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}>Mehr Umsatz umsetzen</h2>
        <p className="text-gray-500 text-sm max-w-lg mx-auto mb-6">Die identifizierten E-Commerce-Hebel können professionell umgesetzt werden – mit konkreten Maßnahmen für mehr Vertrauen, höhere Conversion und mehr Umsatz.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => generateECommercePdf(data as unknown as Record<string, unknown>)}
            className="text-white font-bold text-sm uppercase tracking-widest px-8 py-4 transition-colors"
            style={{ backgroundColor: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
            📄 PDF-Report herunterladen
          </button>
          <a href="https://www.werbeagentur-larissa-girenko.de/kontakt" target="_blank" rel="noopener noreferrer"
            className="inline-block text-white font-bold text-sm tracking-widest uppercase px-10 py-4"
            style={{ backgroundColor: C.teal }}>Beratungsgespräch anfragen →</a>
        </div>
      </section>
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────────
export default function ECommerceHome() {
  const { user } = useAuth();
  const isAdminUser = user?.role === "admin";
  useEffect(() => applySeo({
    title: "E-Commerce-Barometer™ – Mehr Umsatz aus deinem Online-Shop | WERBEAGENTUR Larissa Girenko",
    description: "120+ E-Commerce-Kriterien: Vertrauen, Checkout, Produktseiten, Conversion. E-Commerce-Barometer™ zeigt genau, was dich Umsätze kostet. Vollanalyse ab 97 €.",
    path: "/ecommerce-barometer",
  }), []);
  const [url, setUrl] = useState("");
  const [siteType, setSiteType] = useState<"auto" | "shop" | "dienstleister">("auto");
  const [fullResult, setFullResult] = useState<FullECommerceData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const analyze = trpc.ecommerceBarometer.analyze.useMutation({
    onSuccess: (res) => {
      setIsLoading(false);
      if (res.success && res.data) {
        setFullResult(res.data as unknown as FullECommerceData);
      } else {
        setError((res as any).error || "Vollanalyse fehlgeschlagen");
      }
    },
    onError: (err) => { setIsLoading(false); setError(err.message); },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim() || !isAdminUser) return;
    setError(null);
    setIsLoading(true);
    analyze.mutate({ url: url.trim(), siteType });
  };

  const handleReset = () => {
    setFullResult(null);
    setError(null);
    setIsLoading(false);
    setUrl("");
  };
  const handleBooking = () => {
    const bookingUrl = url.trim()
      ? `${MEETERGO_URL}&notes=${encodeURIComponent("Website: " + url.trim())}`
      : MEETERGO_URL;
    window.open(bookingUrl, "_blank", "noopener,noreferrer");
  };
  const createCheckout = trpc.payment.createCheckout.useMutation({
    onSuccess: (data) => {
      if (data.checkoutUrl) window.open(data.checkoutUrl, "_blank");
    },
    onError: (err) => toast.error(err.message),
  });
  const handleBuy = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "ecommerce_analyse",
      targetUrl: url.trim() || undefined,
      returnPath: "/ecommerce-barometer",
    });
  };
  const handleBuyVideoCall = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "ecommerce_mit_videocall",
      targetUrl: url.trim() || undefined,
      returnPath: "/ecommerce-barometer",
    });
  };

  if (isLoading) return <LoadingScreen url={url} />;
  if (fullResult) return <FullECommerceResults data={fullResult} onReset={handleReset} />;

  return (
    <div className="min-h-screen" style={{ background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/ecommerce-barometer" accentColor={C.accent} />

      {/* Hero – schwarzer Block mit Bild (wie Conversion-Barometer) */}
      <section className="w-full bg-black overflow-hidden">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-stretch" style={{ minHeight: "480px" }}>
          {/* Mobil: Titel über Bild */}
          <div className="block md:hidden px-8 pt-10 pb-6 z-10">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
              E-Commerce-Barometer™ · Werbeagentur Larissa Girenko
            </p>
            <h1 className="font-black uppercase leading-none mb-4 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.6rem, 10vw, 4rem)" }}>
              E-Commerce<br />Barometer™
            </h1>
            <p className="text-gray-300 text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Dein Online-Shop oder Verkaufsprozess wird auf 120+ Kriterien in 8 Dimensionen analysiert – für mehr Vertrauen, höhere Conversion und mehr Umsatz.
            </p>
          </div>

          {/* Bild */}
          <div className="w-full md:w-[42%] md:order-2 relative overflow-hidden" style={{ minHeight: "280px" }}>
            <div className="absolute inset-0 z-10" style={{ background: "linear-gradient(to right, rgba(0,0,0,0.6) 0%, transparent 60%)" }} />
            <div className="absolute top-0 left-0 right-0 h-1 z-20" style={{ backgroundColor: C.accent }} />
            <img
              src={PORTRAIT_IMG}
              alt="Larissa Girenko – E-Commerce-Expertin"
              className="w-full h-full object-cover object-top"
              style={{ minHeight: "280px" }}
            />
          </div>

          {/* Desktop: Text links */}
          <div className="hidden md:flex flex-1 flex-col justify-center px-8 md:px-16 py-14 md:order-1 z-10">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
              E-Commerce-Barometer™ · Werbeagentur Larissa Girenko
            </p>
            <h1 className="font-black uppercase leading-none mb-5 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.4rem, 5.5vw, 4rem)" }}>
              E-Commerce<br />Barometer™
            </h1>
            <p className="text-gray-300 text-sm leading-relaxed mb-8 max-w-md" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Dein Online-Shop oder Verkaufsprozess wird auf 120+ Kriterien in 8 Dimensionen analysiert – für mehr Vertrauen, höhere Conversion und mehr Umsatz.
            </p>
            <div className="flex flex-wrap gap-5">
              <div className="border-l-2 pl-4" style={{ borderColor: C.accent }}>
                <p className="text-white font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>8 Dimensionen</p>
                <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>120+ Kriterien · Shops & Dienstleister</p>
              </div>
              <div className="border-l-2 pl-4" style={{ borderColor: C.accent }}>
                <p className="text-white font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Sofortergebnis</p>
                <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>Kostenlose Vorschau · Vollversion 97 €</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vertrauens-Badge */}
      <div className="flex justify-center py-5 border-b border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-yellow-500 text-sm">&#9733;&#9733;&#9733;&#9733;&#9733;</span>
            <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>5,0 Kundenbewertung</span>
          </div>
          <div className="w-px h-4 bg-gray-300 hidden sm:block" />
          <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>17+ Jahre Erfahrung</span>
          <div className="w-px h-4 bg-gray-300 hidden sm:block" />
          <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>Persoenliche Analyse von Larissa</span>
        </div>
      </div>

      {/* Lead-Magnet Hauptsektion */}
      <section className="max-w-3xl mx-auto w-full px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>
            Kostenlose Erstberatung
          </p>
          <h2 className="font-black uppercase leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 6vw, 3.5rem)" }}>
            Ich analysiere das<br />fuer dich
          </h2>
          <p className="text-gray-500 text-sm max-w-lg mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            Trag deine Website-URL ein und buche deinen kostenlosen 30-Minuten-Termin. Ich analysiere deinen Shop oder deine Dienstleistungsseite persoenlich nach 120+ E-Commerce-Kriterien.
          </p>
        </div>

        {/* Was ich analysiere */}
        <div className="border border-gray-200 p-6 mb-8" style={{ backgroundColor: C.bgGray }}>
          <p className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Was ich fuer dich analysiere</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: "V", title: "Vertrauen & Trust", desc: "Bewertungen, Garantien, Zahlungsarten" },
              { icon: "C", title: "Checkout & Warenkorb", desc: "Baymard Institute Standards, Reibungsfreiheit" },
              { icon: "P", title: "Produktpraesentation", desc: "Bilder, Beschreibungen, USPs" },
              { icon: "M", title: "Mobile Commerce", desc: "Touch-Optimierung, Ladezeit, UX" },
              { icon: "D", title: "Dienstleister-Seiten", desc: "Pakete, Anfragen, Conversion-Pfade" },
              { icon: "S", title: "SEO & Sichtbarkeit", desc: "Strukturierte Daten, Meta-Tags, Speed" },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3 bg-white p-3 border border-gray-100">
                <span className="text-sm font-black flex-shrink-0 w-6 h-6 flex items-center justify-center" style={{ backgroundColor: C.accentLight, color: C.accent }}>{item.icon}</span>
                <div>
                  <p className="font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.title}</p>
                  <p className="text-xs text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Formular + CTA */}
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Deine Website-URL (optional)
            </label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleBooking()}
              placeholder="https://dein-shop.de"
              className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Art deiner Website (optional)
            </label>
            <select
              value={siteType}
              onChange={(e) => setSiteType(e.target.value as "auto" | "shop" | "dienstleister")}
              className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none bg-white"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              <option value="auto">Automatisch erkennen</option>
              <option value="shop">Online-Shop</option>
              <option value="dienstleister">Dienstleister</option>
            </select>
          </div>

          {isAdminUser ? (
            <div className="space-y-3">
              <div className="border border-dashed border-gray-300 px-4 py-3 text-center">
                <p className="text-xs text-gray-500 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Admin-Modus: Vollanalyse fuer Kunden
                </p>
              </div>
              <button
                onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
                disabled={!url.trim() || isLoading}
                className="w-full text-white font-bold text-sm uppercase tracking-widest px-6 py-4 transition-opacity disabled:opacity-40"
                style={{ backgroundColor: C.accent, fontFamily: "'DM Sans', sans-serif" }}
              >
                {isLoading ? "Analysiert ..." : "E-Commerce-Analyse starten"}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs font-bold tracking-widest uppercase text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Wähle deine Option</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border-2 border-gray-200 p-5 flex flex-col">
                  <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vollständige Analyse</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Alle Befunde, priorisierte Empfehlungen und persönlicher PDF-Report. Lieferung in 72 Stunden.</p>
                  <div className="text-xl font-black mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.accent }}>{formatPrice(BAROMETER_PRICES.ecommerce)}</div>
                  <button onClick={handleBuy} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: C.accent, fontFamily: "'DM Sans', sans-serif" }}>Analyse buchen</button>
                </div>
                <div className="border-2 p-5 flex flex-col relative" style={{ borderColor: "#6d0000", backgroundColor: "#6d000008" }}>
                  <div className="absolute -top-3 left-4 px-3 py-1 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
                  <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Analyse + VideoCall</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Vollständige Analyse + 30-minütige persönliche Ergebnis-Interpretation mit Larissa per VideoCall.</p>
                  <div className="text-xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#6d0000" }}>{formatPrice(BAROMETER_MIT_VIDEOCALL)}</div>
                  <p className="text-xs text-gray-400 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 30-Min-VideoCall (+79 €)</p>
                  <button onClick={handleBuyVideoCall} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Analyse + VideoCall buchen</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Social Proof */}
        <div className="mt-12 border-t border-gray-200 pt-8">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: "Wir haben unsere Conversion-Rate in 6 Wochen verdoppelt.", name: "Anna M., Online-Shop" },
              { quote: "Der Report hat genau gezeigt, warum Kunden den Checkout abbrechen.", name: "Felix K., E-Commerce" },
              { quote: "Endlich konkrete Massnahmen fuer meinen Dienstleistungs-Funnel.", name: "Maria S., Beraterin" },
            ].map((item, i) => (
              <div key={i} className="border-l-2 pl-4" style={{ borderColor: C.accent }}>
                <p className="text-sm italic text-gray-600 mb-2 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.quote}</p>
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.accent, fontFamily: "'DM Sans', sans-serif" }}>{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Cross-Selling */}
      <CrossSelling
        headline="Shop analysiert – jetzt alles optimieren"
        subline="Diese Barometer ergänzen das E-Commerce-Barometer perfekt für maximalen Umsatz."
        items={[
          {
            icon: "/manus-storage/33_d51b7819.svg",
            title: "Conversion-Barometer™",
            description: "Psychologische Hebel für mehr Conversions – Cialdini, Kahneman, Baymard Institute. Direkt umsetzbar.",
            price: formatPrice(BAROMETER_PRICES.conversion),
            href: "/conversion-barometer",
            accent: "#b45309",
            badge: "Perfekte Ergänzung",
          },
          {
            icon: "/manus-storage/23_1ab7147e.svg",
            title: "SEO-Barometer™",
            description: "Mehr organischer Traffic in deinen Shop – 100+ SEO-Kriterien für mehr Sichtbarkeit.",
            price: formatPrice(BAROMETER_PRICES.seo),
            href: "/seo-barometer",
            accent: "#0a5c36",
          },
          {
            icon: "/manus-storage/29_7f3bb394.svg",
            title: "Datenschutz-Barometer™",
            description: "DSGVO-Konformität für deinen Shop – Pflicht für jeden Online-Händler. Jetzt prüfen.",
            price: formatPrice(BAROMETER_PRICES.datenschutz),
            href: "/datenschutz-barometer",
            accent: "#0f766e",
          },
        ]}
      />
    </div>
  );
}
